import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken, getUserId } from "../services/authorize";

const statusVariant = {
  active: "warning",
  returned: "success",
  overdue: "danger",
  lost: "dark"
};

const statusLabel = {
  active: "กำลังยืม",
  returned: "คืนแล้ว",
  overdue: "เกินกำหนด",
  lost: "สูญหาย"
};

const toDateInputValue = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const formatDateTime = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getDefaultDueDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return toDateInputValue(d);
};

export default function LibraryCirculation() {
  const borrowFormRef = useRef(null);
  const copyCodeSelectRef = useRef(null);
  const borrowerIdInputRef = useRef(null);
  const [copies, setCopies] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingBorrow, setSubmittingBorrow] = useState(false);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [borrowForm, setBorrowForm] = useState({
    copy_code: "",
    borrower_type: "student",
    borrower_idnumber: "",
    due_date: getDefaultDueDate(),
    loan_note: ""
  });
  const [returnForm, setReturnForm] = useState({
    borrower_type: "student",
    borrower_idnumber: "",
    loan_no: "",
    return_note: ""
  });

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const [copiesRes, loansRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getCopies`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getLoans`, { headers })
      ]);

      const nextCopies = copiesRes.data || [];
      const nextLoans = loansRes.data || [];
      setCopies(nextCopies);
      setLoans(nextLoans);

      setBorrowForm((prev) => ({
        ...prev,
        copy_code: prev.copy_code
      }));
    } catch (fetchError) {
      setError("ไม่สามารถโหลดข้อมูลยืม-คืนได้ กรุณาลองใหม่อีกครั้ง");
      console.error("LibraryCirculation fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      copyCodeSelectRef.current?.focus();
    }
  }, [loading]);

  const availableCopies = useMemo(() => {
    return copies.filter((copy) => copy.copy_status === "available");
  }, [copies]);

  const availableCopyOptions = useMemo(
    () =>
      availableCopies.map((copy) => ({
        value: copy.copy_code,
        label: `${copy.copy_code} - ${copy.title_name}`
      })),
    [availableCopies]
  );

  const selectedBorrowCopyOption = useMemo(
    () =>
      availableCopyOptions.find(
        (option) => option.value.toLowerCase() === String(borrowForm.copy_code).toLowerCase()
      ) || null,
    [availableCopyOptions, borrowForm.copy_code]
  );

  const selectedBorrowCopy = useMemo(() => {
    const keyword = borrowForm.copy_code.trim();
    if (!keyword) return null;
    return (
      copies.find((copy) => String(copy.copy_code).toLowerCase() === keyword.toLowerCase()) ||
      null
    );
  }, [copies, borrowForm.copy_code]);

  const handleBorrowerIdEnter = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();

    const borrowerId = borrowForm.borrower_idnumber.trim();
    if (!borrowerId) {
      setError(
        borrowForm.borrower_type === "teacher"
          ? "กรุณากรอกรหัสประจำตัวครู"
          : "กรุณากรอกเลขประจำตัวนักเรียน"
      );
      return;
    }

    if (!selectedBorrowCopy || selectedBorrowCopy.copy_status !== "available") {
      setError("กรุณาสแกนรหัสเล่มที่พร้อมให้ยืมก่อน");
      copyCodeSelectRef.current?.focus();
      return;
    }

    if (!submittingBorrow) {
      borrowFormRef.current?.requestSubmit();
    }
  };

  const unreturnedByBorrower = useMemo(() => {
    if (!returnForm.borrower_idnumber.trim()) return [];
    return loans.filter(
      (loan) =>
        loan.borrower_type === returnForm.borrower_type &&
        loan.borrower_idnumber === returnForm.borrower_idnumber.trim() &&
        loan.loan_status !== "returned"
    );
  }, [loans, returnForm.borrower_type, returnForm.borrower_idnumber]);

  const handleBorrowSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const officerId = getUserId();
    if (!officerId) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    const trimmedCopyCode = borrowForm.copy_code.trim();
    const trimmedBorrowerId = borrowForm.borrower_idnumber.trim();
    if (!trimmedCopyCode || !trimmedBorrowerId || !borrowForm.due_date) {
      setError("กรุณากรอกข้อมูลยืมให้ครบ");
      return;
    }

    try {
      setSubmittingBorrow(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/borrowCopy`,
        {
          copy_code: trimmedCopyCode,
          borrower_type: borrowForm.borrower_type,
          borrower_idnumber: trimmedBorrowerId,
          due_date: borrowForm.due_date,
          loan_note: borrowForm.loan_note || null,
          t_id: officerId
        },
        { headers }
      );

      setSuccess(`บันทึกการยืมสำเร็จ เลขที่รายการ: ${response.data?.loan_no || "-"}`);
      setBorrowForm((prev) => ({
        ...prev,
        copy_code: "",
        borrower_idnumber: "",
        due_date: getDefaultDueDate(),
        loan_note: ""
      }));
      copyCodeSelectRef.current?.focus();
      await fetchData();
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "student not found") {
        setError("ไม่พบเลขประจำตัวนักเรียนที่ระบุ");
      } else if (message === "teacher not found") {
        setError("ไม่พบรหัสประจำตัวครูที่ระบุ");
      } else if (message === "invalid borrower type") {
        setError("ประเภทผู้ยืมไม่ถูกต้อง");
      } else if (message === "copy is not available") {
        setError("เล่มนี้ไม่พร้อมให้ยืม");
      } else if (message === "copy not found") {
        setError("ไม่พบรหัสเล่มที่ระบุ");
      } else if (message === "missing borrow fields") {
        setError("ข้อมูลยืมไม่ครบถ้วน");
      } else {
        setError("บันทึกการยืมไม่สำเร็จ");
      }
      console.error("Borrow submit error:", submitError);
    } finally {
      setSubmittingBorrow(false);
    }
  };

  const handleReturnSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const officerId = getUserId();
    if (!officerId) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!returnForm.loan_no) {
      setError("กรุณาเลือกรายการยืมที่ต้องการคืน");
      return;
    }

    try {
      setSubmittingReturn(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/returnLoan`,
        {
          loan_no: returnForm.loan_no,
          return_note: returnForm.return_note || null,
          t_id: officerId
        },
        { headers }
      );

      setSuccess(`บันทึกการคืนสำเร็จ เลขที่รายการ: ${response.data?.loan_no || "-"}`);
      setReturnForm((prev) => ({
        ...prev,
        borrower_idnumber: "",
        loan_no: "",
        return_note: ""
      }));
      await fetchData();
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "loan not found") {
        setError("ไม่พบเลขที่รายการยืม");
      } else if (message === "loan already returned") {
        setError("รายการนี้ถูกคืนแล้ว");
      } else if (message === "missing return fields") {
        setError("ข้อมูลคืนไม่ครบถ้วน");
      } else {
        setError("บันทึกการคืนไม่สำเร็จ");
      }
      console.error("Return submit error:", submitError);
    } finally {
      setSubmittingReturn(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">งานยืม-คืนหนังสือ</h4>
          <Badge bg="info">Circulation</Badge>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Card>
              <Card.Header className="fw-semibold">บันทึกรายการยืม</Card.Header>
              <Card.Body>
                <Form ref={borrowFormRef} onSubmit={handleBorrowSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>รหัสเล่ม</Form.Label>
                    <Select
                      ref={copyCodeSelectRef}
                      options={availableCopyOptions}
                      value={selectedBorrowCopyOption}
                      onChange={(option) => setBorrowForm((prev) => ({ ...prev, copy_code: option?.value || "" }))}
                      isSearchable
                      isClearable
                      placeholder="พิมพ์ค้นหารหัสเล่ม เช่น BK-0001"
                      noOptionsMessage={() => "ไม่พบรหัสเล่มที่พร้อมให้ยืม"}
                    />
                    <Form.Text className="text-muted">
                      พิมพ์ค้นหารหัสเล่มเพื่อเลือกได้ทันที (เล่มพร้อมให้ยืม: {availableCopies.length.toLocaleString("th-TH")} เล่ม)
                    </Form.Text>
                  </Form.Group>

                  {borrowForm.copy_code.trim() && !selectedBorrowCopy && (
                    <Alert variant="warning" className="mb-3">
                      ไม่พบรหัสเล่มที่ระบุ
                    </Alert>
                  )}

                  {selectedBorrowCopy && (
                    <Card className="mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                      <Card.Body>
                        <div className="mb-2">
                          <strong>หนังสือ:</strong> {selectedBorrowCopy.title_name}
                        </div>
                        <div className="mb-2">
                          <strong>รหัสเล่ม:</strong> {selectedBorrowCopy.copy_code}
                        </div>
                        <div>
                          <strong>สถานะ:</strong>{" "}
                          <Badge
                            bg={selectedBorrowCopy.copy_status === "available" ? "success" : "secondary"}
                          >
                            {selectedBorrowCopy.copy_status === "available" ? "พร้อมให้ยืม" : selectedBorrowCopy.copy_status}
                          </Badge>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label className="d-block">ผู้ยืม</Form.Label>
                    <div className="d-flex gap-3 mb-2">
                      <Form.Check
                        inline
                        type="radio"
                        id="borrower-student"
                        label="นักเรียน"
                        name="borrower_type"
                        checked={borrowForm.borrower_type === "student"}
                        onChange={() => setBorrowForm((prev) => ({ ...prev, borrower_type: "student" }))}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="borrower-teacher"
                        label="ครูอาจารย์"
                        name="borrower_type"
                        checked={borrowForm.borrower_type === "teacher"}
                        onChange={() => setBorrowForm((prev) => ({ ...prev, borrower_type: "teacher" }))}
                      />
                    </div>
                    <Form.Control
                      ref={borrowerIdInputRef}
                      type="text"
                      value={borrowForm.borrower_idnumber}
                      onChange={(event) => setBorrowForm((prev) => ({ ...prev, borrower_idnumber: event.target.value }))}
                      onKeyDown={handleBorrowerIdEnter}
                      placeholder={borrowForm.borrower_type === "teacher" ? "เช่น T000123" : "เช่น 0001234567890"}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>กำหนดคืน</Form.Label>
                    <Form.Control
                      type="date"
                      value={borrowForm.due_date}
                      onChange={(event) => setBorrowForm((prev) => ({ ...prev, due_date: event.target.value }))}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>หมายเหตุ</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={borrowForm.loan_note}
                      onChange={(event) => setBorrowForm((prev) => ({ ...prev, loan_note: event.target.value }))}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    disabled={
                      submittingBorrow ||
                      !selectedBorrowCopy ||
                      selectedBorrowCopy.copy_status !== "available"
                    }
                  >
                    {submittingBorrow ? "กำลังบันทึก..." : "บันทึกการยืม"}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <Card.Header className="fw-semibold">บันทึกรายการคืน</Card.Header>
              <Card.Body>
                <Form onSubmit={handleReturnSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-block">ผู้ยืม</Form.Label>
                    <div className="d-flex gap-3 mb-2">
                      <Form.Check
                        inline
                        type="radio"
                        id="return-borrower-student"
                        label="นักเรียน"
                        name="return_borrower_type"
                        checked={returnForm.borrower_type === "student"}
                        onChange={() => setReturnForm((prev) => ({ ...prev, borrower_type: "student", loan_no: "" }))}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="return-borrower-teacher"
                        label="ครูอาจารย์"
                        name="return_borrower_type"
                        checked={returnForm.borrower_type === "teacher"}
                        onChange={() => setReturnForm((prev) => ({ ...prev, borrower_type: "teacher", loan_no: "" }))}
                      />
                    </div>
                    <Form.Control
                      type="text"
                      value={returnForm.borrower_idnumber}
                      onChange={(event) => setReturnForm((prev) => ({ ...prev, borrower_idnumber: event.target.value, loan_no: "" }))}
                      placeholder={returnForm.borrower_type === "teacher" ? "เช่น T000123" : "เช่น 0001234567890"}
                    />
                  </Form.Group>

                  {returnForm.borrower_idnumber.trim() && unreturnedByBorrower.length === 0 && (
                    <Alert variant="info" className="mb-3">
                      ไม่พบรายการยืมที่ยังไม่คืนของผู้ยืมนี้
                    </Alert>
                  )}

                  {unreturnedByBorrower.length > 0 && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>เลือกรายการยืมที่ต้องการคืน</Form.Label>
                        <Form.Select
                          value={returnForm.loan_no}
                          onChange={(event) => setReturnForm((prev) => ({ ...prev, loan_no: event.target.value }))}
                          required={unreturnedByBorrower.length > 0}
                        >
                          <option value="">เลือกรายการยืม</option>
                          {unreturnedByBorrower.map((loan) => (
                            <option key={loan.loan_id} value={loan.loan_no}>
                              {loan.loan_no} - {loan.title_name} ({loan.overdue_days > 0 || loan.loan_status === "overdue" ? `เกินกำหนด ${loan.overdue_days || 0} วัน` : "กำลังยืม"})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      {returnForm.loan_no && (() => {
                        const selectedLoan = unreturnedByBorrower.find((loan) => loan.loan_no === returnForm.loan_no);
                        return selectedLoan ? (
                          <Card className="mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                            <Card.Body>
                              <div className="mb-2">
                                <strong>ผู้ยืม:</strong> {selectedLoan.borrower_name} ({selectedLoan.borrower_idnumber})
                              </div>
                              <div className="mb-2">
                                <strong>หนังสือ:</strong> {selectedLoan.title_name}
                              </div>
                              <div className="mb-2">
                                <strong>รหัสเล่ม:</strong> {selectedLoan.copy_code}
                              </div>
                              <div className="mb-2">
                                <strong>วันที่ยืม:</strong> {formatDateTime(selectedLoan.loan_date)}
                              </div>
                              <div className="mb-2">
                                <strong>กำหนดคืน:</strong> {toDateInputValue(selectedLoan.due_date)}
                              </div>
                              <div>
                                <strong>สถานะ:</strong>{" "}
                                <Badge bg={selectedLoan.overdue_days > 0 || selectedLoan.loan_status === "overdue" ? "danger" : "warning"}>
                                  {selectedLoan.overdue_days > 0 || selectedLoan.loan_status === "overdue"
                                    ? `เกินกำหนด ${selectedLoan.overdue_days || 0} วัน`
                                    : "กำลังยืม"}
                                </Badge>
                              </div>
                            </Card.Body>
                          </Card>
                        ) : null;
                      })()}
                    </>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>หมายเหตุคืน</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={returnForm.return_note}
                      onChange={(event) => setReturnForm((prev) => ({ ...prev, return_note: event.target.value }))}
                    />
                  </Form.Group>

                  <Button variant="success" type="submit" disabled={submittingReturn || !returnForm.loan_no}>
                    {submittingReturn ? "กำลังบันทึก..." : "บันทึกการคืน"}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Header className="fw-semibold d-flex align-items-center justify-content-between">
            <span>รายการยืมล่าสุด</span>
            {loading && <Spinner animation="border" size="sm" />}
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>เลขที่ยืม</th>
                  <th>ผู้ยืม</th>
                  <th>หนังสือ</th>
                  <th>รหัสเล่ม</th>
                  <th>วันที่ยืม</th>
                  <th>กำหนดคืน</th>
                  <th>วันที่คืน</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {!loading && loans.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-3">
                      ไม่พบข้อมูลรายการยืม
                    </td>
                  </tr>
                )}

                {loans.slice(0, 30).map((loan) => (
                  <tr key={loan.loan_id}>
                    <td>{loan.loan_no}</td>
                    <td>
                      {loan.borrower_name}
                      <div className="small text-muted">{loan.borrower_idnumber}</div>
                    </td>
                    <td>{loan.title_name}</td>
                    <td>{loan.copy_code}</td>
                    <td>{formatDateTime(loan.loan_date)}</td>
                    <td>{toDateInputValue(loan.due_date) || "-"}</td>
                    <td>{formatDateTime(loan.returned_date)}</td>
                    <td>
                      <Badge
                        bg={
                          loan.loan_status === "returned"
                            ? "success"
                            : loan.overdue_days > 0
                            ? "danger"
                            : "warning"
                        }
                      >
                        {loan.loan_status === "returned"
                          ? "คืนแล้ว"
                          : loan.overdue_days > 0
                          ? `เกินกำหนด (${loan.overdue_days} วัน)`
                          : "กำลังยืม"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </main>
      <Footer />
    </React.Fragment>
  );
}
